import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Stack,
  SxProps,
  TextField,
  Theme,
  Typography,
} from "@mui/material";

import { CustomFormikSubProps, FormikHelper } from "@/common/helpers/formik";
import { ApiEnumName } from "@/common/services/enum";
import { ValidationHelper } from "@/common/validation";
import { ContractSettingsInputDto, SubmissionRequirement } from "@/core/api/generated/v1";

import ApiEnumAutocomplete from "../../Enum/ApiEnumAutocomplete";
import AppTypography from "../../Text/AppTypography";

interface Props {
  formikProps: CustomFormikSubProps<ContractSettingsInputDto | null | undefined>;
  sx?: SxProps<Theme>;
}

export default function ContractSettingsInput({ formikProps, sx }: Props) {
  const { values, errors, setFieldValue } = formikProps;
  const vehicleStateAckOnCheckOutFormikProps = FormikHelper.getSubProps(
    formikProps,
    "vehicleStateAckOnCheckOut",
    (x) => x.vehicleStateAckOnCheckOut,
  );

  return (
    <Stack gap={2} sx={{ ...sx }}>
      {/* Contract timeline settings */}
      <Stack gap={1} sx={{ width: "100%" }}>
        <Typography variant='subtitle1'>Timeline</Typography>

        <Box>
          <FormControl fullWidth>
            <FormControlLabel
              sx={{ width: "fit-content" }}
              label='Require contract start date'
              control={
                <Checkbox
                  checked={values?.isRequireStartDate ?? false}
                  onChange={(e) => setFieldValue("isRequireStartDate", e.target.checked)}
                />
              }
            />
            {errors?.isRequireStartDate && (
              <FormHelperText error>
                {ValidationHelper.getFormikErrorsAsString(errors?.isRequireStartDate)}
              </FormHelperText>
            )}
          </FormControl>

          <FormControl fullWidth>
            <FormControlLabel
              sx={{ width: "fit-content" }}
              label='Require contract end date'
              control={
                <Checkbox
                  checked={values?.isRequireEndDate ?? false}
                  onChange={(e) => setFieldValue("isRequireEndDate", e.target.checked)}
                />
              }
            />
            {errors?.isRequireEndDate && (
              <FormHelperText error>
                {ValidationHelper.getFormikErrorsAsString(errors?.isRequireEndDate)}
              </FormHelperText>
            )}
          </FormControl>
        </Box>
      </Stack>

      {/* Check in/out settings */}
      <Stack gap={1} sx={{ width: "100%" }}>
        <Typography variant='subtitle1'>Check in/check out</Typography>

        <Box>
          <FormControl>
            <FormControlLabel
              sx={{ width: "fit-content" }}
              label='Enable check in/check out'
              control={
                <Checkbox
                  checked={values?.isCheckEnabled ?? false}
                  onChange={(e) => {
                    setFieldValue("isCheckEnabled", e.target.checked);
                    if (!e.target.checked) {
                      setFieldValue("isRequireVisualInspectionBeforeCheckOut", false);
                      setFieldValue("isRequireVisualInspectionBeforeCheckIn", false);
                    }
                  }}
                />
              }
            />
            {errors?.isCheckEnabled && (
              <FormHelperText error>
                {ValidationHelper.getFormikErrorsAsString(errors?.isCheckEnabled)}
              </FormHelperText>
            )}
          </FormControl>
        </Box>

        {values?.isCheckEnabled && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xxs: "1fr", md: "1fr 1fr" },
              gap: 2,
            }}
          >
            {/* Check out */}
            <Stack>
              <Typography variant='subtitle2'>Check out</Typography>

              <Box>
                <FormControl fullWidth>
                  <FormControlLabel
                    sx={{ width: "fit-content" }}
                    label='Require visual inspection before check out'
                    control={
                      <Checkbox
                        checked={values?.isRequireVisualInspectionBeforeCheckOut ?? false}
                        onChange={(e) =>
                          setFieldValue("isRequireVisualInspectionBeforeCheckOut", e.target.checked)
                        }
                      />
                    }
                  />
                  {errors?.isRequireVisualInspectionBeforeCheckOut && (
                    <FormHelperText error>
                      {ValidationHelper.getFormikErrorsAsString(
                        errors?.isRequireVisualInspectionBeforeCheckOut,
                      )}
                    </FormHelperText>
                  )}
                </FormControl>

                <FormControl fullWidth>
                  <FormControlLabel
                    sx={{ width: "fit-content", mb: 1 }}
                    label={
                      <AppTypography
                        decoration={{ variant: "helpText" }}
                        tooltipProps={{
                          title:
                            "On check out, customer is shown damage book and acknowledges vehicle state.",
                        }}
                      >
                        Enable vehicle state acknowledgement
                      </AppTypography>
                    }
                    control={
                      <Checkbox
                        checked={values?.vehicleStateAckOnCheckOut?.isEnabled ?? false}
                        onChange={(e) =>
                          setFieldValue(
                            "vehicleStateAckOnCheckOut",
                            e.target.checked
                              ? ({
                                  ...values?.vehicleStateAckOnCheckOut,
                                  isEnabled: true,
                                  signatureReq: SubmissionRequirement.NotRequired,
                                  personalDocumentImageReq: SubmissionRequirement.NotRequired,
                                } satisfies typeof values.vehicleStateAckOnCheckOut)
                              : { isEnabled: false },
                          )
                        }
                      />
                    }
                  />
                  {vehicleStateAckOnCheckOutFormikProps.errors?.isEnabled && (
                    <FormHelperText error>
                      {ValidationHelper.getFormikErrorsAsString(
                        vehicleStateAckOnCheckOutFormikProps.errors?.isEnabled,
                      )}
                    </FormHelperText>
                  )}
                </FormControl>

                {values?.vehicleStateAckOnCheckOut?.isEnabled && (
                  <>
                    <FormControl margin='dense' fullWidth>
                      <ApiEnumAutocomplete
                        type={ApiEnumName.SubmissionRequirement}
                        value={values?.vehicleStateAckOnCheckOut?.signatureReq}
                        label={
                          <AppTypography
                            decoration={{ variant: "helpText" }}
                            tooltipProps={{
                              title: "Customer's manual on-screen signature.",
                            }}
                          >
                            Signature
                          </AppTypography>
                        }
                        textFieldProps={{
                          error: Boolean(vehicleStateAckOnCheckOutFormikProps.errors?.signatureReq),
                          helperText: ValidationHelper.getFormikErrorsAsString(
                            vehicleStateAckOnCheckOutFormikProps.errors?.signatureReq,
                          ),
                        }}
                        onChange={(newValue) => {
                          setFieldValue("vehicleStateAckOnCheckOut.signatureReq", newValue);
                          setFieldValue(
                            "vehicleStateAckOnCheckOut.personalDocumentImageReq",
                            newValue === SubmissionRequirement.NotRequired
                              ? SubmissionRequirement.NotRequired
                              : newValue === SubmissionRequirement.Optional &&
                                  values?.vehicleStateAckOnCheckOut?.personalDocumentImageReq ===
                                    SubmissionRequirement.Required
                                ? SubmissionRequirement.Optional
                                : values?.vehicleStateAckOnCheckOut?.personalDocumentImageReq,
                          );
                        }}
                      />
                    </FormControl>

                    <FormControl margin='dense' fullWidth>
                      <ApiEnumAutocomplete
                        type={ApiEnumName.SubmissionRequirement}
                        value={values?.vehicleStateAckOnCheckOut?.personalDocumentImageReq}
                        disabledEnumValues={
                          values?.vehicleStateAckOnCheckOut?.signatureReq ===
                          SubmissionRequirement.NotRequired
                            ? [SubmissionRequirement.Optional, SubmissionRequirement.Required]
                            : values?.vehicleStateAckOnCheckOut?.signatureReq ===
                                SubmissionRequirement.Optional
                              ? [SubmissionRequirement.Required]
                              : undefined
                        }
                        label={
                          <AppTypography
                            decoration={{ variant: "helpText" }}
                            tooltipProps={{
                              title: "Photos/scans of customer's ID, driver's license, etc.",
                            }}
                          >
                            Personal document(s)
                          </AppTypography>
                        }
                        textFieldProps={{
                          error: Boolean(
                            vehicleStateAckOnCheckOutFormikProps.errors?.personalDocumentImageReq,
                          ),
                          helperText: ValidationHelper.getFormikErrorsAsString(
                            vehicleStateAckOnCheckOutFormikProps.errors?.personalDocumentImageReq,
                          ),
                        }}
                        onChange={(newValue) => {
                          setFieldValue(
                            "vehicleStateAckOnCheckOut.personalDocumentImageReq",
                            newValue,
                          );
                        }}
                      />
                    </FormControl>

                    <FormControl margin='dense' fullWidth>
                      <TextField
                        label={
                          <AppTypography
                            decoration={{ variant: "helpText" }}
                            tooltipProps={{
                              title:
                                "Short text that is shown to a customer during vehicle state acknowledgment and included in the PDF. If not set, default text will be used.",
                            }}
                          >
                            Acknowledgement short text
                          </AppTypography>
                        }
                        value={values?.vehicleStateAckOnCheckOut?.ackShortText}
                        error={Boolean(vehicleStateAckOnCheckOutFormikProps.errors?.ackShortText)}
                        helperText={ValidationHelper.getFormikErrorsAsString(
                          vehicleStateAckOnCheckOutFormikProps.errors?.ackShortText,
                        )}
                        onChange={(e) => {
                          setFieldValue("vehicleStateAckOnCheckOut.ackShortText", e.target.value);
                        }}
                      />
                    </FormControl>
                  </>
                )}
              </Box>
            </Stack>

            {/* Check in */}
            <Stack>
              <Typography variant='subtitle2'>Check in</Typography>

              <Box>
                <FormControl fullWidth>
                  <FormControlLabel
                    sx={{ width: "fit-content" }}
                    label='Require visual inspection before check in'
                    control={
                      <Checkbox
                        checked={values?.isRequireVisualInspectionBeforeCheckIn ?? false}
                        onChange={(e) =>
                          setFieldValue("isRequireVisualInspectionBeforeCheckIn", e.target.checked)
                        }
                      />
                    }
                  />
                  {errors?.isRequireVisualInspectionBeforeCheckIn && (
                    <FormHelperText error>
                      {ValidationHelper.getFormikErrorsAsString(
                        errors?.isRequireVisualInspectionBeforeCheckIn,
                      )}
                    </FormHelperText>
                  )}
                </FormControl>
              </Box>
            </Stack>
          </Box>
        )}
      </Stack>
    </Stack>
  );
}
